import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { walkthroughSessionStore } from "../../lib/walkthroughSessionStore";

interface WalkthroughContextValue {
  isWalkthroughActive: boolean;
  walkthroughStage: "idle" | "intro" | "tour";
  walkthroughStep: number;
  startWalkthrough: () => void;
  beginWalkthrough: () => void;
  endWalkthrough: () => void;
  cancelWalkthrough: () => void;
  setStep: (step: number) => void;
  navigateTo: (page: string, clientId?: string, params?: Record<string, unknown>) => void;
  onFakeSend: () => void;
  registerNavigate: (fn: (page: string, clientId?: string, params?: Record<string, unknown>) => void) => void;
  registerFakeSend: (fn: () => void) => void;
}

const WalkthroughContext = createContext<WalkthroughContextValue>({
  isWalkthroughActive: false,
  walkthroughStage: "idle",
  walkthroughStep: 0,
  startWalkthrough: () => {},
  beginWalkthrough: () => {},
  endWalkthrough: () => {},
  cancelWalkthrough: () => {},
  setStep: () => {},
  navigateTo: () => {},
  onFakeSend: () => {},
  registerNavigate: () => {},
  registerFakeSend: () => {},
});

export const useWalkthroughContext = () => useContext(WalkthroughContext);

export const WalkthroughProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [walkthroughStage, setWalkthroughStage] = useState<"idle" | "intro" | "tour">("idle");
  const [walkthroughStep, setWalkthroughStep] = useState(0);
  const navigateRef = useRef<(page: string, clientId?: string, params?: Record<string, unknown>) => void>(() => {});
  const fakeSendRef = useRef<() => void>(() => {});
  const isWalkthroughActive = walkthroughStage !== "idle";

  const startWalkthrough = useCallback(() => {
    setWalkthroughStep(0);
    setWalkthroughStage("intro");
  }, []);

  const beginWalkthrough = useCallback(() => {
    setWalkthroughStep(0);
    setWalkthroughStage("tour");
  }, []);

  const endWalkthrough = useCallback(() => {
    setWalkthroughStage("idle");
    setWalkthroughStep(0);
    walkthroughSessionStore.setCompleted(true);
  }, []);

  const cancelWalkthrough = useCallback(() => {
    setWalkthroughStage("idle");
    setWalkthroughStep(0);
  }, []);

  const registerNavigate = useCallback((fn: (page: string, clientId?: string, params?: Record<string, unknown>) => void) => {
    navigateRef.current = fn;
  }, []);

  const registerFakeSend = useCallback((fn: () => void) => {
    fakeSendRef.current = fn;
  }, []);

  const navigateTo = useCallback((page: string, clientId?: string, params?: Record<string, unknown>) => {
    navigateRef.current(page, clientId, params);
  }, []);

  const onFakeSend = useCallback(() => {
    fakeSendRef.current();
  }, []);

  return (
    <WalkthroughContext.Provider
      value={{
        isWalkthroughActive,
        walkthroughStage,
        walkthroughStep,
        startWalkthrough,
        beginWalkthrough,
        endWalkthrough,
        cancelWalkthrough,
        setStep: setWalkthroughStep,
        navigateTo,
        onFakeSend,
        registerNavigate,
        registerFakeSend,
      }}
    >
      {children}
    </WalkthroughContext.Provider>
  );
};
